// Deformable battlefield ground: a height + burn + frost map (half-float RGBA texture: R = total height, G = burn, B = deformation only, A = frost (ICE FAMILY, owner 2026-11-03)) stamped by craters, scorch and frost marks
// from Sim.terrain, displacing a dense grass mesh in the vertex stage with normals rebuilt from the map.
// Also provides CPU-side height samples so heroes, foes and debris sit on the deformed surface.
import * as THREE from 'three/webgpu';
import { float, positionLocal, texture, transformNormalToView, vec2, vec3, normalize } from 'three/tsl';

export const GROUND_HALF = 180;         // map covers [-HALF, HALF] on x and z (arena 160 m + the largest crater reach)
/** ground deformation limits: rims stay under the actors, bowls shallow enough that floating actors still read */
export const MAX_RAISE = 0.45, MAX_DEPTH = 2.2 * 1.1;
/** owner 2026-11-02: 'increase all default crater deformation done by skills by 10%' — every skill crater, trench and dent
 *  digs (and lips) 10% more (the depth cap rose with it; rims stay capped below the actors' feet by MAX_RAISE) */
export const DEFORM_K = 1.1;
export const GROUND_RES = 500;         // texels per side (~0.72 m; was 360 over +-130 m)

export class GroundDeform {
  /** deformation (craters, trenches) on top of the base elevation; heals toward 0 */
  readonly heights = new Float32Array(GROUND_RES * GROUND_RES);
  /** base elevation: the rolling hills from Sim.terrain.hills (owner 2026-11-02), fixed for the run */
  readonly base = new Float32Array(GROUND_RES * GROUND_RES);
  readonly burn = new Float32Array(GROUND_RES * GROUND_RES);
  /** ICE FAMILY (owner 2026-11-03): frost on the grass (rime, spike fields, lance trails, shatters); heals like scorch */
  readonly frost = new Float32Array(GROUND_RES * GROUND_RES);
  readonly tex: THREE.DataTexture;
  private half16 = new Uint16Array(GROUND_RES * GROUND_RES * 4);
  private dirty = false;
  private scarred = false; private healAcc = 0; private healTicks = 0;
  private sinceUpload = 0;
  /** PERF-STRESS (2026-11-02): rows changed since the last upload — only those are re-encoded to half floats */
  private rowLo = 0; private rowHi = GROUND_RES - 1;
  private markRows(j0: number, j1: number): void { if (j0 < this.rowLo) this.rowLo = j0; if (j1 > this.rowHi) this.rowHi = j1; if (j0 < this.scarLo) this.scarLo = j0; if (j1 > this.scarHi) this.scarHi = j1; }
  /** rows that may hold non-zero deformation / burn (the heal pass walks only these) */
  private scarLo = 0; private scarHi = GROUND_RES - 1;
  /** bumped whenever heights change (debris collider rebuilds watch this) */
  version = 0;

  constructor() {
    this.tex = new THREE.DataTexture(this.half16, GROUND_RES, GROUND_RES, THREE.RGBAFormat, THREE.HalfFloatType);
    this.tex.magFilter = THREE.LinearFilter; this.tex.minFilter = THREE.LinearFilter;
    this.tex.wrapS = this.tex.wrapT = THREE.ClampToEdgeWrapping;
    this.tex.needsUpdate = true;
  }

  reset(): void { this.heights.fill(0); this.burn.fill(0); for (const r of this.ripples) r.on = false; this.wave?.fill(0); this.frost.fill(0); this.dirty = true; this.markRows(0, GROUND_RES - 1); this.version++; }

  /** Bake the base elevation (the sim's hills) at every texel centre; called when a new run's terrain appears. */
  setBase(height: (x: number, z: number) => number): void {
    const N = GROUND_RES, k = N / (GROUND_HALF * 2), B = this.base;
    // BIGMAP (2026-11-02): 500² texels × ~30 mounds made this ~27 ms per run start. The hills are broad Gaussians (≥ 9 m), so
    // evaluate every other texel exactly and fill the ones between with the midpoint (error well under 1 cm).
    const at = (i: number, j: number) => height((i + 0.5) / k - GROUND_HALF, (j + 0.5) / k - GROUND_HALF);
    for (let j = 0; j < N; j += 2) {
      const r = j * N;
      for (let i = 0; i < N; i += 2) B[r + i] = at(i, j);
      for (let i = 1; i < N; i += 2) B[r + i] = i + 1 < N ? (B[r + i - 1] + B[r + i + 1]) * 0.5 : at(i, j);
    }
    for (let j = 1; j < N; j += 2) {
      const r = j * N;
      if (j + 1 < N) for (let i = 0; i < N; i++) B[r + i] = (B[r - N + i] + B[r + N + i]) * 0.5;
      else for (let i = 0; i < N; i++) B[r + i] = at(i, j);
    }
    this.dirty = true; this.markRows(0, GROUND_RES - 1); this.sinceUpload = 1; this.version++;
  }

  private toIdx(x: number, z: number): [number, number] {
    const k = GROUND_RES / (GROUND_HALF * 2);
    return [(x + GROUND_HALF) * k - 0.5, (z + GROUND_HALF) * k - 0.5];
  }

  /** Bowl + raised lip. Bowls take the deeper value; lips add up (capped) so overlapping blasts carve a churned field. */
  stampCrater(x: number, z: number, r: number, deep = 1): void {
    const depth = Math.min(r * 0.3 * deep * DEFORM_K, MAX_DEPTH), lip = Math.min(r * 0.08 * Math.min(1.5, deep) * DEFORM_K, MAX_RAISE); // deep: Zenith digs deeper
    this.stamp(x, z, r * 1.7, (d) => {
      const u = d / r;
      const bowl = u < 1 ? -depth * (1 - u * u) * (0.85 + 0.15 * Math.cos(u * 9)) : 0;
      const rim = lip * Math.exp(-(((u - 1.05) / 0.28) ** 2));
      return [bowl, rim, u < 1.25 ? 1 : Math.max(0, 1 - (u - 1.25) / 0.45)];
    });
    this.version++;
  }

  /** Nova Lance trench (owner 2026-11-01: 'the beam, trench, crater, deformation underneath it'): one continuous U-channel
   *  from (x0,z0) along unit (dx,dz) for len metres, half-width w, with raised dirt lips on both sides and charred floor.
   *  Takes the deeper value, so re-stamping while the beam holds is idempotent; it heals like other scars. */
  stampTrench(x0: number, z0: number, dx: number, dz: number, len: number, w: number): void {
    const N = GROUND_RES, k = N / (GROUND_HALF * 2), depth = Math.min(MAX_DEPTH, (0.2 + w * 0.55) * DEFORM_K), lip = Math.min(MAX_RAISE, (0.18 + w * 0.12) * DEFORM_K);
    const reach = w * 1.9, x1 = x0 + dx * len, z1 = z0 + dz * len;
    const [ia, ja] = this.toIdx(Math.min(x0, x1) - reach, Math.min(z0, z1) - reach), [ib, jb] = this.toIdx(Math.max(x0, x1) + reach, Math.max(z0, z1) + reach);
    const i0 = Math.max(0, Math.floor(ia)), i1 = Math.min(N - 1, Math.ceil(ib)), j0 = Math.max(0, Math.floor(ja)), j1 = Math.min(N - 1, Math.ceil(jb));
    for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
      const wx = (i + 0.5) / k - GROUND_HALF, wz = (j + 0.5) / k - GROUND_HALF, rx = wx - x0, rz = wz - z0;
      const t = rx * dx + rz * dz; if (t < -reach || t > len + reach) continue;
      const tc = Math.max(0, Math.min(len, t)), s = Math.hypot(rx - dx * tc, rz - dz * tc);
      // ragged edge: wobble the width along the trench; the mouth near the hands tapers in, the far end rounds off
      const wob = w * (0.88 + 0.12 * Math.sin(t * 1.7 + 0.6) + 0.08 * Math.sin(t * 4.3));
      const taper = Math.min(1, Math.max(0, t) / 2.5), u = s / wob;
      if (u > 1.9) continue;
      const idx = j * N + i; let h = this.heights[idx];
      if (u < 1) h = Math.min(h, -depth * taper * (1 - u ** 2.5));
      else h = Math.max(h, Math.min(MAX_RAISE, lip * taper * Math.exp(-(((u - 1.15) / 0.3) ** 2))));
      this.heights[idx] = Math.min(h, MAX_RAISE);
      const b = u < 1.25 ? 1 : Math.max(0, 1 - (u - 1.25) / 0.6);
      if (b > this.burn[idx]) this.burn[idx] = b;
    }
    this.dirty = true; this.scarred = true; this.markRows(j0, j1); this.version++;
  }

  /** A very shallow dent (Pulse Nova, owner 2026-11-02: 'a slight crater… very slightly'): a soft bowl of the given depth
   *  with a faint raised lip; repeated pulses on the same spot keep the same depth, and it heals like other scars. */
  stampDent(x: number, z: number, r: number, depth: number): void {
    depth *= DEFORM_K;
    const lip = depth * 0.3;
    this.stamp(x, z, r * 1.4, (d) => {
      const u = d / r;
      const bowl = u < 1 ? -depth * (1 - u * u) ** 1.5 : 0;
      const rim = lip * Math.exp(-(((u - 1.08) / 0.22) ** 2));
      return [bowl, rim, 0];
    });
    this.version++;
  }

  /** Char the grass (beam trails, blast rims). */
  stampBurn(x: number, z: number, r: number): void {
    // flat-topped stamp; burn accumulates (capped) so overlapping trail marks merge into one continuous char
    this.stamp(x, z, r, (d) => [0, 0, Math.min(1, Math.max(0, (1 - d / r) * 2.2))], true);
  }
  /** ICE FAMILY (owner 2026-11-03): frost the grass (rime cones, spike fields, lance trails, shatters): a flat-topped icy
   *  blue-white mark that accumulates (capped) like scorch and heals with it; k = strength (1 = full frost). */
  stampFrost(x: number, z: number, r: number, k = 1): void {
    const N = GROUND_RES, kk = N / (GROUND_HALF * 2), [cx, cz] = this.toIdx(x, z), rr = r * kk;
    const i0 = Math.max(0, Math.floor(cx - rr)), i1 = Math.min(N - 1, Math.ceil(cx + rr));
    const j0 = Math.max(0, Math.floor(cz - rr)), j1 = Math.min(N - 1, Math.ceil(cz + rr));
    const F = this.frost;
    for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
      const d = Math.hypot(i - cx, j - cz) / kk; if (d > r) continue;
      const f = Math.min(1, Math.max(0, (1 - d / r) * 2.2)) * k, idx = j * N + i;
      F[idx] = Math.min(0.95, F[idx] + f * 0.5);
    }
    this.dirty = true; this.scarred = true; this.markRows(j0, j1);
  }

  private stamp(x: number, z: number, reach: number, f: (d: number) => [number, number, number], accumulate = false): void {
    const N = GROUND_RES, k = N / (GROUND_HALF * 2), [cx, cz] = this.toIdx(x, z), rr = reach * k;
    const i0 = Math.max(0, Math.floor(cx - rr)), i1 = Math.min(N - 1, Math.ceil(cx + rr));
    const j0 = Math.max(0, Math.floor(cz - rr)), j1 = Math.min(N - 1, Math.ceil(cz + rr));
    for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
      const d = Math.hypot(i - cx, j - cz) / k;
      if (d > reach) continue;
      const [bowl, rim, b] = f(d), idx = j * N + i;
      let h = this.heights[idx];
      // owner 2026-11-01: 'after the crater gets too deep the map turns brown, can't see my player or enemies'. Rims used to
      // ADD up (cap 1.6 m), so overlapping blasts raised a dirt plateau above the actors (who don't follow the render ground).
      // Rims now take the taller value and never exceed MAX_RAISE, which stays below the actors' feet/hover height.
      // 2026-11-03 (Roaring Meteor rework, 'ground deformation/crater'): the lip is a Gaussian, so `rim` was > 0 on every
      // texel and the taller-value rule overwrote every bowl with a hair of lip: craters never dug, they only raised rims.
      // Inside the bowl the lip now rides on the bowl (deeper value wins); outside it the taller value wins, as intended.
      if (bowl < 0) h = Math.min(h, bowl + Math.min(rim, MAX_RAISE) + Math.max(0, h) * 0.3);
      else if (rim > 0) h = Math.max(h, Math.min(rim, MAX_RAISE));
      if (h > MAX_RAISE) h = MAX_RAISE;
      this.heights[idx] = h;
      if (accumulate) this.burn[idx] = Math.min(0.92, this.burn[idx] + b * 0.45);
      else if (b > this.burn[idx]) this.burn[idx] = b;
    }
    this.dirty = true; this.scarred = true; this.markRows(j0, j1);
  }

  // ---- Roaring Meteor (owner 2026-11-03): transient ring-shaped height waves ('the ground rippling as each ring passes') ----
  /** a wave rolling out from (x, z) at `speed` m/s until rMax: a crest `amp` m tall and `width` m wide (fading as it spreads).
   *  Drawn only: it is added at upload time and never enters the scar map (no heal, no debris collider rebuild); sample()
   *  ignores it so actors never bob. At most RIPPLES at once; the band is ≤ (2 rMax)² texels, cleared and restamped per frame. */
  private ripples: { on: boolean; x: number; z: number; speed: number; rMax: number; amp: number; width: number; t: number; i0: number; i1: number; j0: number; j1: number }[] = [];
  private wave: Float32Array | null = null;
  /** QA: ripples started / live */
  rippleCount = 0;
  ripple(x: number, z: number, speed: number, rMax: number, amp: number, width: number, delay = 0): void {
    let r = this.ripples.find((q) => !q.on);
    if (!r) { if (this.ripples.length >= 6) return; r = { on: false, x: 0, z: 0, speed: 1, rMax: 1, amp: 0, width: 1, t: 0, i0: 0, i1: -1, j0: 0, j1: -1 }; this.ripples.push(r); }
    Object.assign(r, { on: true, x, z, speed, rMax: Math.min(rMax, 40), amp, width: Math.max(0.8, width), t: -delay, i0: 0, i1: -1, j0: 0, j1: -1 });
    this.wave ??= new Float32Array(GROUND_RES * GROUND_RES);
    this.rippleCount++;
  }
  get ripplesLive(): number { let n = 0; for (const r of this.ripples) if (r.on) n++; return n; }
  private updateRipples(dt: number): void {
    const W = this.wave; if (!W) return;
    const N = GROUND_RES, k = N / (GROUND_HALF * 2);
    for (const r of this.ripples) {
      if (!r.on) continue;
      if (r.i1 >= r.i0) { // clear last frame's band
        for (let j = r.j0; j <= r.j1; j++) for (let i = r.i0; i <= r.i1; i++) W[j * N + i] = 0;
        this.markRows(r.j0, r.j1); this.dirty = true; r.i1 = -1;
      }
      r.t += dt; if (r.t < 0) continue;
      const front = r.speed * r.t;
      if (front > r.rMax + r.width) { r.on = false; continue; }
      const env = r.amp * (1 - Math.min(1, front / (r.rMax + r.width))) ** 0.6;
      const [cx, cz] = this.toIdx(r.x, r.z), reach = (front + r.width) * k;
      const i0 = Math.max(0, Math.floor(cx - reach)), i1 = Math.min(N - 1, Math.ceil(cx + reach)), j0 = Math.max(0, Math.floor(cz - reach)), j1 = Math.min(N - 1, Math.ceil(cz + reach));
      if (i1 < i0 || j1 < j0) continue;
      for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
        const d = Math.hypot(i - cx, j - cz) / k, s = (d - front) / r.width;
        if (s > 1 || s < -1.8) continue;
        // a crest at the front with a shallow trough behind it
        const w = s >= -1 ? Math.cos(s * Math.PI / 2) ** 2 : -0.35 * Math.cos((s + 1.4) * Math.PI / 0.8) ** 2;
        W[j * N + i] = env * w;
      }
      r.i0 = i0; r.i1 = i1; r.j0 = j0; r.j1 = j1;
      this.markRows(j0, j1); this.dirty = true;
    }
  }

  /** Upload at most ~12×/s (beam scorching stamps every few ticks). */
  update(dt: number): void {
    this.updateRipples(dt);
    this.sinceUpload += dt; this.healAcc += dt;
    // owner 2026-11-01: in long fights the whole arena ended up one grey crater. Scars now heal: every ~0.5 s heights
    // relax toward flat (craters ~70% gone after 30 s) and scorch fades (~75% after 30 s), so fresh hits stay readable.
    if (this.healAcc >= 0.5 && this.scarred) {
      const kh = Math.exp(-this.healAcc / 25), kb = Math.exp(-this.healAcc / 22), kf = Math.exp(-this.healAcc / 9), h = this.heights, b = this.burn, fr = this.frost; // frost melts faster than char
      let any = false, first = -1, lastI = -1;
      // BIGMAP: only the rows that hold scars (500² texels now; a whole-map pass every 0.5 s was ~250k texels)
      const sEnd = Math.min(GROUND_RES, this.scarHi + 1) * GROUND_RES;
      for (let i = Math.max(0, this.scarLo) * GROUND_RES; i < sEnd; i++) {
        if (h[i] !== 0) { h[i] = Math.abs(h[i]) < 0.01 ? 0 : h[i] * kh; any = true; lastI = i; if (first < 0) first = i; }
        if (b[i] !== 0) { b[i] = b[i] < 0.01 ? 0 : b[i] * kb; any = true; lastI = i; if (first < 0) first = i; }
        if (fr[i] !== 0) { fr[i] = fr[i] < 0.01 ? 0 : fr[i] * kf; any = true; lastI = i; if (first < 0) first = i; } // ICE FAMILY
      }
      if (first >= 0) { this.markRows(Math.floor(first / GROUND_RES), Math.floor(lastI / GROUND_RES)); this.scarLo = Math.floor(first / GROUND_RES); this.scarHi = Math.floor(lastI / GROUND_RES); }
      else { this.scarLo = GROUND_RES; this.scarHi = -1; }
      this.scarred = any; this.dirty = true; this.healAcc = 0;
      if (++this.healTicks % 8 === 0) this.version++; // let the debris collider catch up every ~4 s, not every tick
    } else if (this.healAcc >= 0.5) this.healAcc = 0;
    if (!this.dirty || this.sinceUpload < 0.08) return;
    this.sinceUpload = 0; this.dirty = false;
    const h = this.heights, b = this.burn, out = this.half16, toH = THREE.DataUtils.toHalfFloat;
    const B = this.base, W = this.wave, fr = this.frost, iEnd = Math.min(GROUND_RES, this.rowHi + 1) * GROUND_RES;
    if (W) for (let i = this.rowLo * GROUND_RES; i < iEnd; i++) { out[i * 4] = toH(B[i] + h[i] + W[i]); out[i * 4 + 1] = toH(b[i]); out[i * 4 + 2] = toH(h[i]); out[i * 4 + 3] = toH(fr[i]); }
    else for (let i = this.rowLo * GROUND_RES; i < iEnd; i++) { out[i * 4] = toH(B[i] + h[i]); out[i * 4 + 1] = toH(b[i]); out[i * 4 + 2] = toH(h[i]); out[i * 4 + 3] = toH(fr[i]); } // A = frost (ICE FAMILY)
    // BIGMAP (2026-11-02): the map grew to 500² texels (2 MB of half floats). Only the re-encoded rows are sent to the GPU
    // (flushed from scene.onBeforeRender, see attach()); the first upload, and any frame without a renderer hook, is whole.
    const lo = this.rowLo, hi = Math.min(GROUND_RES - 1, this.rowHi);
    this.rowLo = GROUND_RES; this.rowHi = -1;
    if (this.attached && this.uploadedOnce && hi >= lo) { this.upLo = Math.min(this.upLo, lo); this.upHi = Math.max(this.upHi, hi); }
    else if (hi >= lo) this.tex.needsUpdate = true;
    this.uploads++;
  }

  /** QA / perf: uploads issued and rows sent by the partial path */
  uploads = 0; rowsSent = 0; fullUploads = 0;
  private attached = false; private uploadedOnce = false; private upLo = GROUND_RES; private upHi = -1;
  /** Partial uploads: the scene's onBeforeRender hands us the renderer each frame; pending rows go straight to the backend
   *  texture (WebGL2: texSubImage2D on the row band; WebGPU: queue.writeTexture on the row band). Three.js r187's node
   *  backends ignore Texture.updateRanges (only the classic WebGLRenderer reads it), so this is done here. Any backend
   *  state we cannot read falls back to a whole upload (needsUpdate). */
  attach(scene: THREE.Scene): void {
    this.attached = true;
    const prev = scene.onBeforeRender;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    scene.onBeforeRender = (renderer: any, ...rest: any[]) => { this.flush(renderer); (prev as (...a: unknown[]) => void).call(scene, renderer, ...rest); };
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private flush(renderer: any): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const be = renderer?.backend as any, data = be?.get?.(this.tex);
    // the real GPU texture exists (not the shared 1×1 placeholder three uses before the first upload)
    const td = renderer?._textures?.get?.(this.tex);
    const ready = !!data && td?.isDefaultTexture === false && (be.isWebGPUBackend ? data.texture?.width === GROUND_RES : data.glFormat !== undefined && !!data.textureGPU);
    if (!ready) { if (this.upHi >= this.upLo) { this.tex.needsUpdate = true; this.fullUploads++; this.upLo = GROUND_RES; this.upHi = -1; } return; }
    this.uploadedOnce = true;
    if (this.upHi < this.upLo) return;
    const y0 = this.upLo, rows = this.upHi - this.upLo + 1, N = GROUND_RES;
    this.upLo = GROUND_RES; this.upHi = -1;
    try {
      if (be.isWebGPUBackend) {
        be.device.queue.writeTexture({ texture: data.texture, mipLevel: 0, origin: { x: 0, y: y0, z: 0 } }, this.half16.buffer,
          { offset: this.half16.byteOffset + y0 * N * 8, bytesPerRow: N * 8, rowsPerImage: rows }, { width: N, height: rows, depthOrArrayLayers: 1 });
      } else {
        const gl = be.gl as WebGL2RenderingContext, st = be.state;
        st.bindTexture(data.glTextureType, data.textureGPU);
        st.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false); st.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false); st.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
        gl.texSubImage2D(data.glTextureType, 0, 0, y0, N, rows, data.glFormat, data.glType, this.half16, y0 * N * 4);
        st.unbindTexture();
      }
      this.rowsSent += rows;
    } catch { this.tex.needsUpdate = true; this.fullUploads++; }
  }

  /** Bilinear ground height (hills + deformation) at a world position (0 outside the map). */
  sample(x: number, z: number): number { return this.bilerp(x, z, this.heights) + this.bilerp(x, z, this.base); }
  /** Bilinear base elevation only (the hills): things the sim places at a height above the ground (shots, hit sparks)
   *  are lifted by this so they ride over hills without dipping into craters. */
  hill(x: number, z: number): number { return this.bilerp(x, z, this.base); }
  private bilerp(x: number, z: number, H: Float32Array): number {
    const N = GROUND_RES, k = N / (GROUND_HALF * 2); let fx = (x + GROUND_HALF) * k - 0.5, fz = (z + GROUND_HALF) * k - 0.5;
    if (fx < 0 || fz < 0 || fx > N - 1 || fz > N - 1) return 0;
    const i = Math.floor(fx), j = Math.floor(fz), tx = fx - i, tz = fz - j;
    const i1 = Math.min(N - 1, i + 1), j1 = Math.min(N - 1, j + 1);
    fx = H[j * N + i] * (1 - tx) + H[j * N + i1] * tx;
    fz = H[j1 * N + i] * (1 - tx) + H[j1 * N + i1] * tx;
    return fx * (1 - tz) + fz * tz;
  }

  /** TSL: the map at a world xz node (vertex stage): x = total height, y = burn, z = crater deformation, w = frost */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sampleAt(xz: any): any {
    return texture(this.tex, xz.add(vec2(GROUND_HALF, GROUND_HALF)).div(float(GROUND_HALF * 2))).level(float(0));
  }

  /** TSL helpers for a material on a mesh whose local space == world space. */
  nodes() {
    const half = GROUND_HALF, texel = (GROUND_HALF * 2) / GROUND_RES, map = this.tex;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sampleAt = (dx: number, dz: number): any => {
      const uvn = positionLocal.xz.add(vec2(dx + half, dz + half)).div(float(half * 2));
      return texture(map, uvn).level(float(0));
    };
    const c = sampleAt(0, 0);
    const h = c.x;
    const position = positionLocal.add(vec3(0, h, 0));
    const hL = sampleAt(-texel, 0).x, hR = sampleAt(texel, 0).x, hD = sampleAt(0, -texel).x, hU = sampleAt(0, texel).x;
    const normal = transformNormalToView(normalize(vec3(hL.sub(hR), float(texel * 2), hD.sub(hU))));
    // soft burn: 5-tap average (centre + 4 diagonals at 0.8 texel) so small scorch marks don't read as texel squares
    const o = texel * 0.8;
    const burnSoft = c.y.mul(2).add(sampleAt(-o, -o).y).add(sampleAt(o, -o).y).add(sampleAt(-o, o).y).add(sampleAt(o, o).y).div(6);
    // ICE FAMILY (owner 2026-11-03): frost (the A channel), softened the same way
    const frostSoft = c.w.mul(2).add(sampleAt(-o, -o).w).add(sampleAt(o, -o).w).add(sampleAt(-o, o).w).add(sampleAt(o, o).w).div(6);
    // deform = craters / trenches only (crater colouring); base = the hills (painted slope / hilltop / dip shading)
    return { position, normal, height: h, burn: c.y, burnSoft, frost: c.w, frostSoft, deform: c.z, base: h.sub(c.z), slope: normalize(vec3(hL.sub(hR), float(texel * 2), hD.sub(hU))).y };
  }
}

/** BIGMAP (2026-11-02): the deformable near ground as TILES instead of one 300×300 plane. The map is 360 m across now; one
 *  plane at the old 0.87 m vertex spacing would be ~176k vertices drawn every frame. 6×6 tiles of 60 m at 70×70 segments keep
 *  0.86 m spacing and are frustum-culled (the follow camera sees 4–9 of them). Edge vertices of neighbouring tiles sit at the
 *  same world xz, so the displaced surface has no seams. Bounding volumes cover the displacement range (hills + rims). */
export const GROUND_TILES = 6, GROUND_TILE_SEGS = 70;
export function nearGroundTiles(mat: THREE.Material, receiveShadow = true): THREE.Group {
  const g = new THREE.Group(); g.name = 'near-ground';
  const T = GROUND_TILES, size = (GROUND_HALF * 2) / T;
  const geo = new THREE.PlaneGeometry(size, size, GROUND_TILE_SEGS, GROUND_TILE_SEGS).rotateX(-Math.PI / 2);
  for (let j = 0; j < T; j++) for (let i = 0; i < T; i++) {
    const cx = -GROUND_HALF + (i + 0.5) * size, cz = -GROUND_HALF + (j + 0.5) * size;
    const tg = geo.clone().translate(cx, 0, cz); // world-space vertices: the ground material samples the map at positionLocal.xz
    tg.boundingBox = new THREE.Box3(new THREE.Vector3(cx - size / 2, -4, cz - size / 2), new THREE.Vector3(cx + size / 2, 6, cz + size / 2));
    tg.boundingSphere = tg.boundingBox.getBoundingSphere(new THREE.Sphere());
    const m = new THREE.Mesh(tg, mat); m.receiveShadow = receiveShadow; m.frustumCulled = true; m.name = 'ground-tile-' + i + '-' + j;
    g.add(m);
  }
  geo.dispose();
  return g;
}
